import PropTypes from 'prop-types';
import { Fzf } from 'fzf';
import { useState, useEffect } from 'react';
import _ from 'lodash';
import { useCombobox } from 'downshift';
import './Combobox.scss';
import LoadingSkeleton from '../LoadingSkeleton';

// Doesn't rely on state so defining up here
const defaultRenderListItems = (item, index, selectedItem, highlightedIndex, listItemClassName, getItemProps) => (
  <li
    className={`
                Combobox-listItem 
                ${
                  (selectedItem?.id && selectedItem?.id === item.id) ||
                  (selectedItem?.value && selectedItem?.value === item.value)
                    ? 'Combobox-listItem--selected'
                    : ''
                }
                ${highlightedIndex === index ? 'Combobox-listItem--highlighted' : ''}
                ${item.disabled ? 'Combobox-listItem--disabled' : ''}
                ${listItemClassName}
              `}
    key={item.id || item.value}
    {...(item.disabled ? {} : getItemProps?.({ item, index }))}
    style={item.disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
  >
    <span className={`ComboxBox-listItemText ${item.disabled ? 'Combobox-listItem--disabledText' : ''}`}>
      {item.name}
    </span>
  </li>
);

function Combobox({
  disabled = false,
  description = '',
  error = '',
  isValid = true,
  items = [],
  label = '',
  loading = false,
  loadingSkeletonDisabled = false,
  placeholder = '',
  tabIndex = 0,
  value = '',
  showRightButton = true,
  clearInputOnFocus = false,
  // defaults
  defaultIsOpen = false,
  preSelectedItem,
  // classes
  className = '',
  buttonClassName = '',
  descriptionClassName = '',
  errorClassName = '',
  inputClassName = '',
  inputRowClassName = '',
  labelClassName = '',
  listClassName = '',
  listItemClassName = '',
  // handlers
  onBlur = () => {},
  onChange = () => {},
  onInputChange = () => {},
  // customization
  renderListItem,
  noResultsItem,
}) {
  const [searchText, setSearchText] = useState('');

  // timed and uses 0-1ms so not worth memoizing right now
  const safeItems = Array.isArray(items) ? items : [];
  const fzf = new Fzf(safeItems, {
    selector: (item) => item?.searchName || item?.name || '',
    casing: 'case-insensitive',
  });

  const filteredItems = fzf.find(searchText || '').map((entry) => entry.item);
  const defaultSelectedItem = preSelectedItem || _.find(safeItems, { id: value }) || _.find(safeItems, { value });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    selectItem,
    setInputValue,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setSearchText(inputValue);
      onInputChange(inputValue);
    },
    onIsOpenChange({ isOpen: nextIsOpen }) {
      if (clearInputOnFocus && nextIsOpen) {
        setInputValue('');
        setSearchText('');
      }
    },
    onSelectedItemChange: (changes) => {
      if (!loading && value !== changes?.selectedItem) {
        //onChange is given the value _and_ a callback it can pass a value to if it needs to update things after the change
        // Is this good? it smells a little but maybe it's okay
        onChange(changes?.selectedItem, setInputValue);
      }
    },
    defaultSelectedItem,
    defaultHighlightedIndex: 0,
    items: filteredItems,
    itemToString(item) {
      return item ? item.name : '';
    },
    defaultIsOpen,
  });

  useEffect(() => {
    selectItem(defaultSelectedItem);
  }, [preSelectedItem, value]);

  if ((loading && !loadingSkeletonDisabled) || !items) {
    return (
      <div className={`Combobox ${className}`}>
        {label && <LoadingSkeleton className="Combobox-label--loader" />}
        <LoadingSkeleton className="Combobox-input--loader" />
        {description && <LoadingSkeleton className="Combobox-description--loader" />}
        {error && <LoadingSkeleton className="Combobox-error--loader" />}
      </div>
    );
  }

  const chosenListItemRenderer =
    renderListItem && typeof renderListItem === 'function' ? renderListItem : defaultRenderListItems;
  const chosenNoResultsItem = noResultsItem || { name: 'No items found', disabled: true };

  return (
    <div className={`Combobox ${className}  ${disabled ? 'Combobox--disabled' : ''}`}>
      <div className={`Comboxbox-field`}>
        {label && (
          <label
            className={`Combobox-label ${!isValid ? 'Combobox-label--error' : ''} ${labelClassName}`}
            {...getLabelProps()}
          >
            {label}
          </label>
        )}
        <div
          className={`Combobox-inputRow ${!isValid ? 'Combobox-inputRow--error' : ''} ${inputRowClassName}`}
          disabled={disabled}
        >
          <input
            placeholder={placeholder}
            className={`Combobox-input ${!isValid ? 'Combobox-input--error' : ''} ${inputClassName} `}
            disabled={disabled}
            {...getInputProps({
              onFocus: () => {
                if (clearInputOnFocus) {
                  setInputValue('');
                  setSearchText('');
                }
              },
              onBlur: (event) => {
                if (clearInputOnFocus) {
                  setInputValue(selectedItem?.name || '');
                  setSearchText('');
                }
                onBlur(event);
              },
              tabIndex: tabIndex,
            })}
          />
          <button
            aria-label="toggle menu"
            className={`Combobox-rightButton ${buttonClassName}`}
            disabled={disabled}
            type="button"
            {...getToggleButtonProps()}
          >
            {showRightButton && (isOpen ? <>&#8593;</> : <>&#8595;</>)}
          </button>
        </div>
      </div>
      {description && !isOpen && <div className={`Combobox-description ${descriptionClassName}`}>{description}</div>}
      {!isValid && !isOpen && <div className={`Combobox-error ${errorClassName}`}>{error}</div>}
      <ul
        className={`Combobox-list ${isOpen ? 'Combobox-list--open' : 'Combobox-list--closed'} ${listClassName}`}
        {...getMenuProps()}
      >
        {isOpen &&
          (filteredItems?.length > 0
            ? filteredItems.map((item, index) =>
                chosenListItemRenderer(item, index, selectedItem, highlightedIndex, listItemClassName, getItemProps),
              )
            : chosenListItemRenderer(
                chosenNoResultsItem,
                0, //hardcoded as it's the only item
                selectedItem,
                highlightedIndex,
                listItemClassName,
                getItemProps,
              ))}
      </ul>
    </div>
  );
}

const itemShape = PropTypes.shape({
  //  expect either id or value as the identifier
  // value is used to make it more compatible with Select/Option setups
  id: PropTypes.string,
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  searchName: PropTypes.string, // not displayed but used for full searches
  disabled: PropTypes.bool,
});

Combobox.propTypes = {
  buttonClassName: PropTypes.string,
  className: PropTypes.string,
  description: PropTypes.string,
  descriptionClassName: PropTypes.string,
  disabled: PropTypes.bool,
  defaultIsOpen: PropTypes.bool,
  error: PropTypes.string,
  errorClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  inputRowClassName: PropTypes.string,
  isValid: PropTypes.bool,
  items: PropTypes.arrayOf(itemShape),
  noResultsItem: itemShape,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  listClassName: PropTypes.string,
  listItemClassName: PropTypes.string,
  loading: PropTypes.bool,
  loadingSkeletonDisabled: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  placeholder: PropTypes.string,
  renderListItem: PropTypes.func,
  preSelectedItem: PropTypes.any,
  showRightButton: PropTypes.bool,
  clearInputOnFocus: PropTypes.bool,
  tabIndex: PropTypes.number,
  value: PropTypes.string,
};

export default Combobox;
